import { cloneDeep } from "lodash-es";
import { pickBy } from "@/helpers/utility/CollectionFunctions";
import { notNull } from "@/helpers/utility/Type";
import { toUid } from "@/helpers/utility/Id";
import { useFlowBranchStore } from "./flowBranches.store";
import {
  buildItemCollisionMap,
  getCollisions
} from "../relation/relation_distribution/CollisionMap";
import { useSceneTree } from "../scene/sceneTree";
import { useSceneFlowNodeStore } from "./sceneFlowNodes";

import type { CollisionMap } from "../relation/relation_distribution/CollisionMap";
import type { FlowItem } from "./FlowItem";
import type { FlowBranchItem } from "./FlowBranchItem";
import type { Uid } from "@/helpers/utility/Id";
import type { SceneTreeItemSize } from "../scene/SceneTreeItem";

const GRID_SIZE = 10;

export function getFlowNetItems(
  flows: { [id: number]: FlowItem },
  nodeSizes: Record<Uid<"sceneNode" | "flow">, SceneTreeItemSize>,
): { [uid: Uid<"flow">]: SceneTreeItemSize<"flow"> } {
  const flowBranchStore = useFlowBranchStore();
  const branches = flowBranchStore.byFlow;
  const sceneTree = useSceneTree();
  const sceneFlowNodeStore = useSceneFlowNodeStore();
  const sceneFlowNodes = sceneFlowNodeStore.byScene[sceneTree.activeScene.id] || {};

  const sizesWithoutFlows = pickBy(nodeSizes, (item) => item.type != "flow");

  const rects: { [uid: Uid<"flow">]: SceneTreeItemSize<"flow"> } = {};

  const fixedFlowItems: { flowId: number; x: number; y: number }[] = [];

  for (const flowId in flows) {
    const flow = flows[flowId];
    const flowNode = sceneFlowNodes[flow.id];
    const flowSize = nodeSizes[flow.uid] as SceneTreeItemSize<"flow"> | undefined;
    const flowBranches = branches[flow.id];

    /**
     * Branches without flows shouldn't exist. Skip rendering them if any are found.
     */
    if (!flowBranches) {
      console.error(`Flow ${flow.id} has no branches, skipping flow rendering.`);
      continue;
    }

    /**
     * Get the elements the branches are connected to.
     */
    const branchElements = flowBranches.map((branch) => {
      const uid = toUid(branch.elementType, branch.elementId);
      const node = sceneTree.elementNodes.get(uid);
      if (!node) return null;
      const size = nodeSizes[node.uid];
      if (!size) return null;
      const center = {
        x: size.x + size.width / 2,
        y: size.y + size.height / 2
      };
      return {
        ...size,
        center
      };
    }).filter(notNull);

    /**
     * If none of the elements the branches are connected to are visible,
     * we do not show the flow. Skip any further calculations.
     */
    if (branchElements.length == 0) {
      console.debug(`Flow ${flow.id} has no visible branch elements.`);
      continue;
    }

    if (flowSize && !flowSize.generated) {
      fixedFlowItems.push({ flowId: flow.id, x: flowSize.x, y: flowSize.y });
      continue;
    }
    if (flowNode) {
      if (flowNode.visible) {
        fixedFlowItems.push({ flowId: flow.id, x: flowNode.x, y: flowNode.y });
      }
      continue;
    }

    const NON_EXISTENT_TARGET_ID = -555;
    const referenceUid = toUid(flow.valueReferenceType ?? "target", flow.valueReferenceId ?? NON_EXISTENT_TARGET_ID);
    const referenceElementNodeItem = sceneTree.elementNodes.get(referenceUid);
    const referencedElementNodeSize = referenceElementNodeItem ? nodeSizes[referenceElementNodeItem.uid] : undefined;


    if (branchElements.length == 1) {
      if (!referencedElementNodeSize || branchElements[0].uid == referencedElementNodeSize?.uid) {
        // move the flow item to the side of the regulator element
        // depending on the flow direction

        const mainFlowType = flowBranches.reduce((total, branch) => {
          total[branch.branchType] += 1;
          if (total.source >= total.sink) {
            total.max = "source";
          } else {
            total.max = "sink";
          }
          return total;
        }, { max: "source", source: 0, sink: 0 }).max;

        const pseudoElement = cloneDeep(branchElements[0]);
        if (mainFlowType == "source") {
          pseudoElement.center.x += pseudoElement.width / 2;
        } else {
          pseudoElement.center.x -= pseudoElement.width / 2;
        }

        branchElements.push(pseudoElement);
      } else if (referencedElementNodeSize) {
        const referencedElementCenter = {
          x: referencedElementNodeSize.x + referencedElementNodeSize.width / 2,
          y: referencedElementNodeSize.y + referencedElementNodeSize.height / 2
        };

        // create a pseudo-element that is moved a little bit
        // to the direction of the regulator element
        const pseudoElement = cloneDeep(branchElements[0]);
        const dx = referencedElementCenter.x - pseudoElement.center.x;
        const dy = referencedElementCenter.y - pseudoElement.center.y;
        // normalize the vector
        const length = Math.sqrt(dx * dx + dy * dy) || 1;
        const normalizedDx = Math.round(dx / length) * 2;
        const normalizedDy = Math.round(dy / length) * 2;
        pseudoElement.center.x += normalizedDx * GRID_SIZE;
        pseudoElement.center.y += normalizedDy * GRID_SIZE;
  
        branchElements.push(pseudoElement);
      }
    }

    const xValues = branchElements.map((element) => element.center.x);
    const yValues = branchElements.map((element) => element.center.y);

    const minX = Math.min(...xValues);
    const minY = Math.min(...yValues);
    const maxX = Math.max(...xValues);
    const maxY = Math.max(...yValues);

    const width = 0;
    const height = 0;

    let x = roundToGrid((minX + maxX) / 2);
    let y = roundToGrid((minY + maxY) / 2);

    rects[flow.uid] = {
      uid: flow.uid,
      type: "flow",

      x,
      y,
      width,
      height,
      left: x,
      top: y,
      right: x + width,
      bottom: y + height,
      center: {
        x: x + width / 2,
        y: y + height / 2
      },
      locked: false,
      superLocked: false,
      generated: true,
      gridLocked: true,

      data: {}
    };
  }

  resolveFlowAnchorPositions(rects, sizesWithoutFlows, branches);

  for (const flowId in rects) {
    roundItemToGrid(rects[flowId as Uid<"flow">]);
  }

  for (const position of fixedFlowItems) {
    const flow = flows[position.flowId];
    const size = nodeSizes[flow.uid] || position;
    const FLOW_WIDTH = 0;
    const FLOW_HEIGHT = 0;
    rects[flow.uid] = {
      uid: flow.uid,
      type: "flow",

      x: size.x,
      y: size.y,
      width: FLOW_WIDTH,
      height: FLOW_HEIGHT,
      left: size.x,
      top: size.y,
      right: size.x + FLOW_WIDTH,
      bottom: size.y + FLOW_HEIGHT,
      center: {
        x: size.x + FLOW_WIDTH / 2,
        y: size.y + FLOW_HEIGHT / 2
      },
      locked: false,
      superLocked: false,
      generated: false,
      gridLocked: false,
      data: {}
    };
  }

  if (Object.values(rects).length > 0) {
    // console.log("resolved flow anchor positions", rects);
  }

  return rects;
}

/**
 * Use a force-based system to resolve the anchor positions of the flow items.
 */
function resolveFlowAnchorPositions(
  flowRects: { [uid: Uid<"flow">]: SceneTreeItemSize<"flow"> },
  sceneNodeSizes: Record<Uid<"sceneNode" | "flow">, SceneTreeItemSize>,
  branchesByFlow: { [flowId: number]: FlowBranchItem[] }
) {
  const flowRectsArray = Object.values(flowRects);
  if (flowRectsArray.length == 0) return;
  
  const collisionMap = buildItemCollisionMap(sceneNodeSizes);

  const flowRectsArrayCopy = flowRectsArray.slice();

  let iterations = 0;

  const iteratedPositions = [flowRectsArray.reduce(
    (collection, flowRect) => {
      collection[flowRect.uid] = {
        x: flowRect.x,
        y: flowRect.y
      };
      return collection;
    },
    {} as { [flowUid: string]: { x: number; y: number } }
  )];

  while (flowRectsArrayCopy.length > 0 && iterations < 100) {
    iterations++;

    iteratedPositions[iterations] = {};

    for (const flowRect of flowRectsArrayCopy) {
      const flowItem = flowRects[flowRect.uid];

      const force = calculatePositionForce(flowItem.center, collisionMap);

      if (force.x == 0 && force.y == 0) {
        // for (const otherFlowRect of flowRectsArray) {
        //   if (otherFlowRect.id == flowRect.id) continue;

        //   // check manually if there is a collision with the flow rect
        //   if (
        //     otherFlowRect.left - GRID_SIZE <= flowItem.right &&
        //     otherFlowRect.right + GRID_SIZE >= flowItem.left &&
        //     otherFlowRect.top - GRID_SIZE <= flowItem.bottom &&
        //     otherFlowRect.bottom + GRID_SIZE >= flowItem.top
        //   ) {
        //     collisions.add(otherFlowRect);
        //   }
        // }

        // If the flow item is in the center of the regulator element, move it to the side
        // if () {
        //   if (referencedElement.uid == collidingItem.uid) {
        //     const mainFlowType = branches.reduce((total, branch) => {
        //       total[branch.branchType] += 1;
        //       if (total.source >= total.sink) {
        //         total.max = "source";
        //       } else {
        //         total.max = "sink";
        //       }
        //       return total;
        //     }, { max: "source", source: 0, sink: 0 }).max;

        //     if (mainFlowType == "source") {
        //       x = right;
        //     } else {
        //       x = -left;
        //     }
        //   } else {
        //     y = -top;
        //   }
        // }

        flowRectsArrayCopy.splice(flowRectsArrayCopy.indexOf(flowRect), 1);
        continue;
      }


      const positionBefore = {
        x: flowRect.x,
        y: flowRect.y
      };

      try {

        moveFlowItem(flowRect, force.x, force.y);

        const newPosition = {
          x: flowRect.x,
          y: flowRect.y
        };

        const lastPosition = iteratedPositions[iterations - 2]?.[flowRect.uid];
        if (newPosition.x == lastPosition?.x && newPosition.y == lastPosition?.y) {
          console.log("movement is oscillating, trying to move to the other direction");
          const x = roundToGrid((positionBefore.x + newPosition.x) / 2);
          const y = roundToGrid((positionBefore.y + newPosition.y) / 2);

          moveFlowItem(flowRect, x - newPosition.x, y - newPosition.y);
          
          // if (absItemForce.x > absItemForce.y) {
          //   force.x = 0;
          //   force.y = GRID_SIZE;
          // } else {
          //   force.x = GRID_SIZE;
          //   force.y = 0;
          // }
          // moveFlowItem(flowRect, force.x, force.y);
        }

        iteratedPositions[iterations][flowRect.uid] = {
          x: flowRect.x,
          y: flowRect.y
        };
        // console.log(
        //   iterations,
        //   "position",
        //   { x: flowRect.x, y: flowRect.y },
        //   { ...lastPosition }
        // );
      } catch (e) {
        console.error(e);
      }
    }
  }
}

/**
 * Calculate all forces influencing a position in the net.
 */
function calculatePositionForce(position: { x: number, y: number}, collisionMap: CollisionMap) {
  const RADIUS = 16 * GRID_SIZE;
  const ITEM_MARGIN = 4 * GRID_SIZE;

  const collisions = getCollisions(
    collisionMap,
    position.x - RADIUS,
    position.y - RADIUS,
    position.x + RADIUS,
    position.y + RADIUS
  );

  // console.log(collisions);

  // console.log("pos", { ...position });

  const itemForces = [...collisions].map((collidingItem) => {
    // check for a collision using the item margin
    if (collidingItem.x - ITEM_MARGIN > position.x) return { x: 0, y: 0 };
    if (collidingItem.x + collidingItem.width + ITEM_MARGIN < position.x) return { x: 0, y: 0 };
    if (collidingItem.y - ITEM_MARGIN > position.y) return { x: 0, y: 0 };
    if (collidingItem.y + collidingItem.height + ITEM_MARGIN < position.y) return { x: 0, y: 0 };

    const OFFSET_SIZE = collidingItem.type == "flow" ? 2 * GRID_SIZE : ITEM_MARGIN + GRID_SIZE;
    const right = Math.max(collidingItem.x + collidingItem.width + OFFSET_SIZE - position.x, 0);
    const left = Math.max(position.x - (collidingItem.x - OFFSET_SIZE), 0);

    const bottom = Math.max(collidingItem.y + collidingItem.height + OFFSET_SIZE - position.y, 0);
    const top = Math.max(position.y - (collidingItem.y - OFFSET_SIZE), 0);

    let x = 0;
    let y = 0;

    if (left > right) {
      x = right;
    } else if (right > left) {
      x = -left;
    }

    if (top > bottom) {
      y = bottom;
    } else if (bottom > top) {
      y = -top;
    }

    if (Math.abs(x) < Math.abs(y) && x != 0) {
      y = 0;
    } else if (Math.abs(y) < Math.abs(x) && y != 0) {
      x = 0;
    }

    // console.log(collidingItem.uid, { x, y }, { left, right, top, bottom }, { left: collidingItem.left - OFFSET_SIZE, right: collidingItem.right + OFFSET_SIZE, top: collidingItem.top - OFFSET_SIZE, bottom: collidingItem.bottom + OFFSET_SIZE });

    return {
      x: x,
      y: y
    };
  });

  type DirectedForce = {
    x: number;
    y: number;
    left: number;
    right: number;
    up: number;
    down: number;
  };
  const absItemForce = itemForces.reduce(
    (totalForce: DirectedForce, itemForce) => {
      return {
        x: totalForce.x + Math.abs(itemForce.x),
        y: totalForce.y + Math.abs(itemForce.y),
        left:
          totalForce.left + (itemForce.x < 0 ? Math.abs(itemForce.x) : 0),
        right:
          totalForce.right + (itemForce.x > 0 ? Math.abs(itemForce.x) : 0),
        up: totalForce.up + (itemForce.y < 0 ? Math.abs(itemForce.y) : 0),
        down:
          totalForce.down + (itemForce.y > 0 ? Math.abs(itemForce.y) : 0)
      };
    },
    { x: 0, y: 0, left: 0, right: 0, up: 0, down: 0 }
  );

  const force = {
    x: 0,
    y: 0
  };

  for (const itemForce of itemForces) {
    force.x =
      Math.abs(force.x) > Math.abs(itemForce.x) ? force.x : itemForce.x;
    force.y =
      Math.abs(force.y) > Math.abs(itemForce.y) ? force.y : itemForce.y;
  }

  if (absItemForce.left && absItemForce.right) {
    force.x = (-absItemForce.left + absItemForce.right) / 2;
  }
  if (absItemForce.up && absItemForce.down) {
    force.y = (-absItemForce.up + absItemForce.down) / 2;
  }

  if (force.x == 0 && force.y == 0) {
    if (absItemForce.x > absItemForce.y) {
      force.y = GRID_SIZE;
    } else if (absItemForce.y > absItemForce.x) {
      force.x = GRID_SIZE;
    }
  }

  return force;
}

function moveFlowItem(flowItem: SceneTreeItemSize<"flow">, dx: number, dy: number) {
  flowItem.x += dx;
  flowItem.y += dy;
  flowItem.left += dx;
  flowItem.top += dy;
  flowItem.right += dx;
  flowItem.bottom += dy;
  flowItem.center.x += dx;
  flowItem.center.y += dy;
}

function roundToGrid(value: number) {
  return Math.round(value / GRID_SIZE) * GRID_SIZE;
}

function roundItemToGrid(item: SceneTreeItemSize<"flow">) {
  item.x = roundToGrid(item.x);
  item.y = roundToGrid(item.y);
  item.width = roundToGrid(item.width);
  item.height = roundToGrid(item.height);
  item.left = item.x;
  item.top = item.y;
  item.right = item.left + item.width;
  item.bottom = item.top + item.height;
  item.center.x = item.left + item.width / 2;
  item.center.y = item.top + item.height / 2;
}
